import { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import * as Leaflet from 'leaflet';

export class DataMarker<T> extends Leaflet.Marker {
	data?: T;

	constructor(
		latLng: Leaflet.LatLngExpression,
		options: (Leaflet.MarkerOptions & { data: T }) | undefined
	) {
		const { data, ...rest } = options ?? {};
		super(latLng, rest);
		this.data = data;
	}
}

type MunicipioFeature = Feature<Polygon | MultiPolygon, { name?: string }>;

type Caixa = { x1: number; y1: number; x2: number; y2: number };

const ALTURA_ROTULO = 14;
const LARGURA_POR_CARACTERE = 6;

function centroideDoAnel(anel: number[][]) {
	let areaDuplicada = 0;
	let somaX = 0;
	let somaY = 0;

	for (let i = 0, j = anel.length - 1; i < anel.length; j = i++) {
		const cruzado = anel[j][0] * anel[i][1] - anel[i][0] * anel[j][1];
		areaDuplicada += cruzado;
		somaX += (anel[j][0] + anel[i][0]) * cruzado;
		somaY += (anel[j][1] + anel[i][1]) * cruzado;
	}

	if (areaDuplicada === 0) {
		return { area: 0, centro: Leaflet.latLng(anel[0][1], anel[0][0]) };
	}

	return {
		area: Math.abs(areaDuplicada / 2),
		centro: Leaflet.latLng(
			somaY / (3 * areaDuplicada),
			somaX / (3 * areaDuplicada)
		),
	};
}

function centroideDaFeature(feature: MunicipioFeature) {
	const { geometry } = feature;
	const aneisExternos =
		geometry.type === 'Polygon'
			? [geometry.coordinates[0]]
			: geometry.coordinates.map(poligono => poligono[0]);

	return aneisExternos
		.map(anel => centroideDoAnel(anel))
		.reduce((maior, atual) => (atual.area > maior.area ? atual : maior));
}

function rotuloDivIcon(nome: string) {
	const conteudo = document.createElement('span');
	conteudo.textContent = nome;

	return Leaflet.divIcon({
		className: 'rotulo-municipio',
		html: conteudo,
		iconSize: [120, ALTURA_ROTULO],
		iconAnchor: [60, ALTURA_ROTULO / 2],
	});
}

function sobrepoe(a: Caixa, b: Caixa) {
	return !(a.x2 < b.x1 || a.x1 > b.x2 || a.y2 < b.y1 || a.y1 > b.y2);
}

class RotulosMunicipios extends Leaflet.LayerGroup {
	private readonly itens: {
		marcador: Leaflet.Marker;
		largura: number;
	}[];
	private mapa?: Leaflet.Map;

	constructor(municipios: FeatureCollection, pane: string) {
		super();

		this.itens = (municipios.features as MunicipioFeature[])
			.filter(feature => !!feature.properties?.name)
			.map(feature => {
				const nome = feature.properties.name!;
				const { centro, area } = centroideDaFeature(feature);

				return {
					area,
					largura: nome.length * LARGURA_POR_CARACTERE,
					marcador: Leaflet.marker(centro, {
						pane,
						interactive: false,
						keyboard: false,
						icon: rotuloDivIcon(nome),
					}),
				};
			})
			.sort((a, b) => b.area - a.area);

		this.itens.forEach(({ marcador }) => this.addLayer(marcador));
	}

	override onAdd(mapa: Leaflet.Map) {
		super.onAdd(mapa);
		this.mapa = mapa;
		mapa.on('zoomend', this.resolverSobreposicao, this);
		this.resolverSobreposicao();
		return this;
	}

	override onRemove(mapa: Leaflet.Map) {
		mapa.off('zoomend', this.resolverSobreposicao, this);
		this.mapa = undefined;
		return super.onRemove(mapa);
	}

	private resolverSobreposicao() {
		if (!this.mapa) {
			return;
		}

		const ocupadas: Caixa[] = [];

		this.itens.forEach(({ marcador, largura }) => {
			const elemento = marcador.getElement();
			if (!elemento) {
				return;
			}

			const ponto = this.mapa!.latLngToLayerPoint(marcador.getLatLng());
			const caixa: Caixa = {
				x1: ponto.x - largura / 2,
				y1: ponto.y - ALTURA_ROTULO / 2,
				x2: ponto.x + largura / 2,
				y2: ponto.y + ALTURA_ROTULO / 2,
			};

			const colide = ocupadas.some(outra => sobrepoe(caixa, outra));
			elemento.style.display = colide ? 'none' : '';

			if (!colide) {
				ocupadas.push(caixa);
			}
		});
	}
}

export function criarCamadaRotulosMunicipios(
	municipios: FeatureCollection,
	pane = 'labels'
): Leaflet.LayerGroup {
	return new RotulosMunicipios(municipios, pane);
}
